import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaPen, FaTrash, FaTag, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { createPlan, getPlans, updatePlan, deletePlan } from '../../../api/services/billingService';
import Modal from '../../../components/Modal/Modal';
import './Plans.css';

const EMPTY_FORM = {
  title: '',
  description: '',
  amount: '',
  discount: 0,
  billingCycle: 'MONTHLY',
  active: true,
};

const BILLING_LABELS = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Semi-Annual',
  YEARLY: 'Yearly',
};

const formatAmount = (amount) =>
  new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(Number(amount) || 0);

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Shared create/edit form body. */
const PlanForm = ({ formData, onChange, onSubmit, onCancel, loading, submitLabel }) => (
  <form className="plan-form" onSubmit={onSubmit}>
    <div className="plan-form-row">
      <div className="plan-field">
        <label className="plan-label">Plan Title *</label>
        <input type="text" name="title" className="plan-input" placeholder="e.g. Monthly Landlord" value={formData.title} onChange={onChange} disabled={loading} />
      </div>
      <div className="plan-field">
        <label className="plan-label">Billing Cycle *</label>
        <select name="billingCycle" className="plan-input plan-select" value={formData.billingCycle} onChange={onChange} disabled={loading}>
          <option value="MONTHLY">Monthly</option>
          <option value="QUARTERLY">Quarterly</option>
          <option value="SEMI_ANNUAL">Semi-Annual</option>
          <option value="YEARLY">Yearly</option>
        </select>
      </div>
    </div>

    <div className="plan-field">
      <label className="plan-label">Description *</label>
      <textarea name="description" className="plan-input plan-textarea" rows="3" placeholder="Short description of the plan" value={formData.description} onChange={onChange} disabled={loading} />
    </div>

    <div className="plan-form-row">
      <div className="plan-field">
        <label className="plan-label">Amount (KES) *</label>
        <input type="number" name="amount" className="plan-input" placeholder="1000" value={formData.amount} onChange={onChange} min="0" step="100" disabled={loading} />
      </div>
      <div className="plan-field">
        <label className="plan-label">Discount (%)</label>
        <input type="number" name="discount" className="plan-input" placeholder="0" value={formData.discount} onChange={onChange} min="0" max="100" step="1" disabled={loading} />
      </div>
    </div>

    <label className="plan-checkbox">
      <input type="checkbox" name="active" checked={formData.active} onChange={onChange} disabled={loading} />
      <span>Active plan (available for subscription)</span>
    </label>

    <div className="plan-form-actions">
      {onCancel ? (
        <button type="button" className="plan-btn ghost" onClick={onCancel} disabled={loading}>Cancel</button>
      ) : null}
      <button type="submit" className="plan-btn primary" disabled={loading}>
        {loading ? 'Saving…' : submitLabel}
      </button>
    </div>
  </form>
);

const Plans = () => {
  const [allPlans, setAllPlans] = useState([]);
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createLoading, setCreateLoading] = useState(false);

  const [editPlan, setEditPlan] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editLoading, setEditLoading] = useState(false);

  const [deletingPlanId, setDeletingPlanId] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const fetchAllPlans = useCallback(async () => {
    setFetchingPlans(true);
    try {
      const response = await getPlans({ page: currentPage, size: pageSize });
      if (response.status === 'success' && response.data) {
        setAllPlans(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalElements(response.data.totalElements || 0);
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to fetch plans' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'An error occurred while fetching plans' });
    } finally {
      setFetchingPlans(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchAllPlans();
  }, [fetchAllPlans]);

  const handleFormChange = (setter) => (e) => {
    const { name, value, type, checked } = e.target;
    setter((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = (form) => {
    if (!form.title.trim()) return 'Plan title is required';
    if (!form.description.trim()) return 'Plan description is required';
    if (!form.amount || Number(form.amount) <= 0) return 'Plan amount must be greater than 0';
    if (form.discount < 0 || form.discount > 100) return 'Discount must be between 0 and 100';
    return null;
  };

  const openCreate = () => {
    setCreateForm(EMPTY_FORM);
    setMessage({ type: '', text: '' });
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const err = validate(createForm);
    if (err) { setMessage({ type: 'error', text: err }); return; }
    setCreateLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await createPlan({
        title: createForm.title,
        description: createForm.description,
        amount: Number(createForm.amount),
        discount: Number(createForm.discount),
        billingCycle: createForm.billingCycle,
        active: createForm.active,
      });
      if (response.status === 'success' && response.data) {
        setMessage({ type: 'success', text: `Plan "${createForm.title}" created.` });
        setCreateOpen(false);
        setCreateForm(EMPTY_FORM);
        setCurrentPage(0);
        await fetchAllPlans();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to create plan' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'An error occurred while creating the plan' });
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (plan) => {
    setEditPlan(plan);
    setEditForm({
      title: plan.title || '',
      description: plan.description || '',
      amount: plan.amount ?? '',
      discount: plan.discount ?? 0,
      billingCycle: plan.billingCycle || 'MONTHLY',
      active: Boolean(plan.active),
    });
    setMessage({ type: '', text: '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editPlan?.id) return;
    const err = validate(editForm);
    if (err) { setMessage({ type: 'error', text: err }); return; }
    setEditLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await updatePlan(editPlan.id, {
        title: editForm.title,
        description: editForm.description,
        amount: Number(editForm.amount),
        discount: Number(editForm.discount || 0),
        billingCycle: editForm.billingCycle,
        active: editForm.active,
      });
      if (response.status === 'success' && response.data) {
        setMessage({ type: 'success', text: 'Plan updated.' });
        setEditPlan(null);
        await fetchAllPlans();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to update plan' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'An error occurred while updating the plan' });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!plan?.id) return;
    if (!window.confirm(`Delete plan "${plan.title}"? This cannot be undone.`)) return;
    setDeletingPlanId(plan.id);
    setMessage({ type: '', text: '' });
    try {
      await deletePlan(plan.id);
      setMessage({ type: 'success', text: 'Plan deleted.' });
      await fetchAllPlans();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete plan' });
    } finally {
      setDeletingPlanId(null);
    }
  };

  return (
    <div className="plans-container">
      <div className="plans-header">
        <div>
          <h1 className="page-title">Plans Management</h1>
          <p className="plans-subtitle">Create and manage subscription plans for Landlords and BnB Hosts.</p>
        </div>
        <button type="button" className="plan-btn primary" onClick={openCreate}>
          <FaPlus /> New Plan
        </button>
      </div>

      {message.text && (
        <div className={`plan-alert ${message.type === 'success' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      {fetchingPlans ? (
        <div className="plans-state">Loading plans…</div>
      ) : allPlans.length === 0 ? (
        <div className="plans-empty">
          <FaTag className="plans-empty-icon" />
          <p>No plans yet.</p>
          <button type="button" className="plan-btn primary" onClick={openCreate}><FaPlus /> Create your first plan</button>
        </div>
      ) : (
        <>
          <div className="plans-count">{totalElements} plan{totalElements === 1 ? '' : 's'}</div>
          <div className="plans-grid">
            {allPlans.map((plan) => {
              const hasDiscount = Number(plan.discount) > 0;
              return (
                <div key={plan.id} className={`plan-card ${plan.active ? '' : 'inactive'}`}>
                  <div className="plan-card-top">
                    <span className={`plan-status ${plan.active ? 'active' : 'inactive'}`}>
                      {plan.active ? <FaCheckCircle /> : <FaTimesCircle />}
                      {plan.active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="plan-cycle">{BILLING_LABELS[plan.billingCycle] || plan.billingCycle}</span>
                  </div>

                  <h3 className="plan-name">{plan.title}</h3>
                  <p className="plan-description">{plan.description}</p>

                  <div className="plan-price">
                    <span className="plan-price-amount">{formatAmount(plan.effectiveAmount ?? plan.amount)}</span>
                    {hasDiscount && (
                      <span className="plan-price-original">{formatAmount(plan.amount)}</span>
                    )}
                  </div>
                  {hasDiscount && <span className="plan-discount-badge">{plan.discount}% off</span>}

                  <div className="plan-meta">
                    <div><span>Duration</span><strong>{plan.durationDays ? `${plan.durationDays} days` : '—'}</strong></div>
                    <div><span>Created</span><strong>{formatDate(plan.createdAt)}</strong></div>
                  </div>

                  <div className="plan-card-actions">
                    <button type="button" className="plan-btn outline" onClick={() => openEdit(plan)}>
                      <FaPen /> Edit
                    </button>
                    <button
                      type="button"
                      className="plan-btn danger"
                      onClick={() => handleDelete(plan)}
                      disabled={deletingPlanId === plan.id}
                    >
                      <FaTrash /> {deletingPlanId === plan.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="plans-pagination">
              <button className="plan-btn ghost" onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} disabled={currentPage === 0}>← Previous</button>
              <span className="plans-page-info">Page {currentPage + 1} of {totalPages}</span>
              <button className="plan-btn ghost" onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))} disabled={currentPage === totalPages - 1}>Next →</button>
            </div>
          )}
        </>
      )}

      {createOpen && (
        <Modal onClose={() => !createLoading && setCreateOpen(false)} className="plan-modal" ariaLabel="Create plan">
          <div className="plan-modal-header">
            <h3>Create New Plan</h3>
          </div>
          <PlanForm
            formData={createForm}
            onChange={handleFormChange(setCreateForm)}
            onSubmit={handleCreate}
            onCancel={() => setCreateOpen(false)}
            loading={createLoading}
            submitLabel="Create Plan"
          />
        </Modal>
      )}

      {editPlan && (
        <Modal onClose={() => !editLoading && setEditPlan(null)} className="plan-modal" ariaLabel={`Edit plan ${editPlan.title}`}>
          <div className="plan-modal-header">
            <h3>Edit Plan</h3>
          </div>
          <PlanForm
            formData={editForm}
            onChange={handleFormChange(setEditForm)}
            onSubmit={handleEdit}
            onCancel={() => setEditPlan(null)}
            loading={editLoading}
            submitLabel="Save Changes"
          />
        </Modal>
      )}
    </div>
  );
};

export default Plans;
